import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart, type CartItem } from "@/hooks/useCart";
import { useAuth } from "@/hooks/useAuth";
import { useUserPrice } from "@/hooks/useUserPrice";
import { createOrderFromCart, getOrderById, getOrderItems, toMoney } from "@/services/supabaseService";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function Cart() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { items, removeFromCart, updateQuantity, totalCount, totalAmount, totalSubscriptionAmount, totalSalesRefund, clearCart } = useCart();
  const { getPrice, getProfit } = useUserPrice();
  const [showSuccess, setShowSuccess] = useState(false);
  const [successOrder, setSuccessOrder] = useState<{
    orderId: string;
    amount: number;
    profit: number;
  } | null>(null);
  const [error, setError] = useState("");
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [limitTip, setLimitTip] = useState<string | null>(null);

  const showLimitTip = (msg: string) => {
    setLimitTip(msg);
    setTimeout(() => setLimitTip(null), 2500);
  };

  const safeUpdateQuantity = (productId: string, quantity: number) => {
    const result = updateQuantity(productId, quantity);
    if (result.wasLimited) {
      showLimitTip(result.message || "普通分銷全站限購 10 組商品");
    }
    return result;
  };

  const getMaxQtyForUser = (item: CartItem) => {
    if (user && user.level === "普通分銷") return 10;
    return item.product.stock > 0 ? item.product.stock : 999;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-24 text-center py-20">
          <p className="text-gray-500 text-lg mb-4">請先登入分銷商帳號</p>
          <button onClick={() => navigate("/login")} className="px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium cursor-pointer">前往登入</button>
        </div>
        <Footer />
      </div>
    );
  }

  // VIP 區域權限限制：普通分銷僅能購買「體驗認購區」
  const vipRestrictedItems = user && user.level === "普通分銷"
    ? items.filter((item) => item.product.region !== "體驗認購區")
    : [];
  const hasVipRestrictedItems = vipRestrictedItems.length > 0;

  const handleCheckout = async () => {
    setError("");
    if (items.length === 0) return;

    // VIP 區域權限檢查
    if (hasVipRestrictedItems) {
      const names = vipRestrictedItems.map((item) => `「${item.product.name}」`).join("、");
      setError(`此區域僅限 VIP 會員認購，請聯繫管理員升級：${names}`);
      return;
    }

    // Pre-check: validate stock against latest product data in cart items
    const stockIssues = items
      .filter((item) => item.product.stock <= 0)
      .map((item) => `「${item.product.name}」已售完`);
    if (stockIssues.length > 0) {
      setError(`部分商品無庫存，無法下單：${stockIssues.join("、")}`);
      return;
    }

    setIsCheckingOut(true);
    try {
      const cartItems = items.map((item) => ({
        productId: item.productId,
        productName: item.product.name,
        productImage: item.product.images[0] || "",
        distributionPrice: getPrice(item.product),
        suggestedPrice: item.product.suggestedPrice,
        quantity: item.quantity,
      }));

      const result = await createOrderFromCart(user.id, user.name, cartItems);
      if (!result.success) {
        // 直接顯示後端回傳的具體錯誤訊息（含錯誤代碼）
        setError(result.error || "下單失敗");
        setIsCheckingOut(false);
        return;
      }

      clearCart();
      // 刷新用戶餘額（即時反映在 UI 上）
      await refreshUser();

      // 從資料庫讀取真實訂單資料，確保顯示金額與 total_amount 完全一致
      let realAmount = 0;
      let realProfit = 0;
      try {
        const orderId = result.orderId || "";
        const realOrder = await getOrderById(orderId);
        if (realOrder) {
          realAmount = toMoney(realOrder.total_amount);
          const realItems = await getOrderItems(orderId);
          realProfit = realItems.reduce(
            (sum, item) => sum + toMoney(item.suggested_price) * toMoney(item.quantity),
            0
          );
        }
      } catch {
        // 若讀取失敗，回退到 cart 計算值（不應該發生）
      }

      setSuccessOrder({
        orderId: result.orderId || "",
        amount: realAmount,
        profit: realProfit,
      });
      setShowSuccess(true);
    } catch (err: unknown) {
      console.error("[Cart.handleCheckout] 未捕獲異常:", err);
      let displayMsg = "下單失敗";
      if (err instanceof Error && err.message) {
        displayMsg = err.message;
      } else if (typeof err === "string") {
        displayMsg = err;
      } else if (err && typeof err === "object") {
        try { displayMsg = JSON.stringify(err); } catch { /* ignore */ }
      }
      setError(displayMsg);
    } finally {
      setIsCheckingOut(false);
    }
  };

  if (showSuccess && successOrder) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="pt-24 pb-12">
          <div className="px-6 lg:px-12 max-w-lg mx-auto">
            <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-emerald-50 rounded-full flex items-center justify-center">
                <i className="ri-check-line text-emerald-600 text-2xl"></i>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">下單成功！</h2>
              <p className="text-gray-500 text-sm mb-1">
                訂單 <span className="font-medium text-gray-700">{successOrder.orderId}</span> 已建立
              </p>
              <p className="text-gray-500 text-sm mb-2">
                共 ${successOrder.amount} 已從錢包扣除。
              </p>
              <p className="text-sm text-emerald-600 mb-6">
                銷售後返款：${successOrder.profit}
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => navigate("/dashboard/orders")}
                  className="px-5 py-2.5 rounded-xl bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 cursor-pointer"
                >
                  查看訂單
                </button>
                <button
                  onClick={() => navigate("/products")}
                  className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 cursor-pointer"
                >
                  繼續選品
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      {limitTip && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap">
          {limitTip}
        </div>
      )}

      <div className="pt-20 pb-12">
        <div className="px-6 lg:px-12 max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-6 text-sm text-gray-500">
            <button onClick={() => navigate("/dashboard")} className="hover:text-amber-600 cursor-pointer">個人中心</button>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-gray-700">購物車</span>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-6">購物車 ({totalCount})</h1>

          {items.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-shopping-cart-line text-4xl text-gray-300"></i>
              </div>
              <p className="text-gray-500 text-lg mb-4">購物車是空的</p>
              <button
                onClick={() => navigate("/products")}
                className="px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium hover:bg-amber-600 cursor-pointer"
              >
                去選品
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Items */}
              <div className="lg:col-span-2 space-y-3">
                {items.map((item) => (
                  <div key={item.productId} className="bg-white rounded-xl border border-gray-100 p-4 flex gap-4">
                    <img
                      src={item.product.images[0]}
                      alt={item.product.name}
                      className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-800 line-clamp-1">{item.product.name}</h3>
                      <p className="text-xs text-gray-400 mt-1">認購價 ${(getPrice(item.product) || 0).toLocaleString('zh-TW')} · 銷售 ${(Number(item.product.suggestedPrice) || 0).toLocaleString('zh-TW')}</p>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                          <button
                            onClick={() => safeUpdateQuantity(item.productId, Math.max(0, item.quantity - 1))}
                            className="w-8 h-8 flex items-center justify-center text-gray-500 hover:bg-gray-50 cursor-pointer"
                          >
                            <i className="ri-subtract-line text-xs"></i>
                          </button>
                          <input
                            type="number"
                            min={0}
                            max={999}
                            value={item.quantity}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === "") {
                                safeUpdateQuantity(item.productId, 0);
                                return;
                              }
                              const val = parseInt(raw, 10);
                              if (Number.isNaN(val)) return;
                              const maxAllowed = getMaxQtyForUser(item);
                              if (user && user.level === "普通分銷" && val > 10) {
                                safeUpdateQuantity(item.productId, 10);
                                showLimitTip("普通分銷最高認購 10 組");
                                return;
                              }
                              if (val > maxAllowed) {
                                safeUpdateQuantity(item.productId, maxAllowed);
                              } else {
                                safeUpdateQuantity(item.productId, Math.max(0, val));
                              }
                            }}
                            className="w-12 text-center text-sm outline-none border-none bg-transparent"
                          />
                          <button
                            onClick={() => {
                              const maxAllowed = getMaxQtyForUser(item);
                              if (user && user.level === "普通分銷" && item.quantity >= 10) {
                                showLimitTip("普通分銷最高認購 10 組");
                                return;
                              }
                              safeUpdateQuantity(item.productId, Math.min(maxAllowed, item.quantity + 1));
                            }}
                            className="w-8 h-8 flex items-center justify-center text-gray-500 hover:bg-gray-50 cursor-pointer"
                          >
                            <i className="ri-add-line text-xs"></i>
                          </button>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-gray-900">${(Math.round(getPrice(item.product) * item.quantity) || 0).toLocaleString('zh-TW')}</p>
                          <p className="text-xs text-emerald-600">利潤 ${(Math.round(getProfit(item.product) * item.quantity) || 0).toLocaleString('zh-TW')}</p>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.productId)}
                      className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-red-500 self-start cursor-pointer"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div className="space-y-4">
                <div className="bg-white rounded-xl border border-gray-100 p-5">
                  <h2 className="text-sm font-semibold text-gray-900 mb-4">訂單摘要</h2>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">認購總額</span>
                      <span className="font-medium">${totalSubscriptionAmount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">銷售總額</span>
                      <span className="font-medium text-gray-700">${totalSalesRefund}</span>
                    </div>
                    <div className="border-t border-gray-100 pt-2 mt-2">
                      <div className="flex justify-between">
                        <span className="text-emerald-600 font-medium">銷售後返款</span>
                        <span className="font-bold text-emerald-600">+${totalSalesRefund}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-stone-50 rounded-lg">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-gray-500">錢包餘額</span>
                      <span className={`text-xs font-medium ${toMoney(user.balance) >= totalSubscriptionAmount ? "text-emerald-600" : "text-red-500"}`}>
                        ${toMoney(user.balance)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">扣款後餘額</span>
                      <span className={`text-xs font-bold ${toMoney(user.balance) - totalSubscriptionAmount <= 0 ? "text-red-600" : "text-gray-700"}`}>
                        ${Math.max(0, toMoney(user.balance) - totalSubscriptionAmount)}
                      </span>
                    </div>
                    {Number(user.balance) < totalSubscriptionAmount && (
                      <button
                        onClick={() => navigate("/dashboard/recharge")}
                        className="w-full mt-2 py-1.5 rounded-lg bg-amber-100 text-amber-700 text-xs font-medium hover:bg-amber-200 cursor-pointer"
                      >
                        餘額不足，立即儲值
                      </button>
                    )}
                  </div>

                  <p className="mt-3 text-xs text-gray-400 leading-relaxed">
                    下單後，請聯繫團長提供最終收件人資訊及物流安排。
                  </p>

                  {error && (
                    <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-600">
                      <div className="flex items-start gap-2">
                        <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <i className="ri-error-warning-line text-red-500"></i>
                        </div>
                        <span className="break-words">{error}</span>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleCheckout}
                    disabled={items.length === 0 || isCheckingOut || hasVipRestrictedItems}
                    className="w-full mt-4 py-3 rounded-xl bg-amber-500 text-white font-medium text-sm hover:bg-amber-600 transition-all active:scale-[0.98] disabled:opacity-60 cursor-pointer whitespace-nowrap"
                  >
                    {isCheckingOut ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center animate-spin"><i className="ri-loader-2-line text-sm"></i></div>
                        處理中...
                      </span>
                    ) : hasVipRestrictedItems ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-lock-line text-sm"></i></div>
                        僅限VIP會員認購
                      </span>
                    ) : (
                      "確認下單並扣款"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
